import { request, type ApiFetch, type ApiResult } from './client.ts'

// Typed client for the bookings dashboard read (INT-432).
//
// Same transcription rule as api/webhooks.ts: the service's zod schemas are
// module-private and importing back is a Rush cycle, so the shapes below are
// transcribed from `surfaces/rest/schemas.ts` (`bookingSchema`) and
// `surfaces/rest/bookings.ts`. A Booking is the published Payload shape with
// additive dashboard fields; rows come from the shared `payloads` store, a
// legacy API delivery, or the dispatch service, and `source` says which.

/** `dispatch`: a payload the dispatch service holds and no one has booked yet —
 *  every payload created through the API since the cut, until it books. */
export type BookingSource = 'payload' | 'delivery' | 'dispatch'

/** The published payload-status vocabulary — legacy delivery statuses arrive
 *  already translated into it by the service. */
export type BookingStatus =
  | 'draft'
  | 'ready'
  | 'scheduled'
  | 'assigned_to_driver'
  | 'pickup_in_transit'
  | 'pickup_arrived'
  | 'picked_up'
  | 'dropoff_in_transit'
  | 'dropoff_arrived'
  | 'return_in_transit'
  | 'return_arrived'
  | 'holding'
  | 'delivered'
  | 'returned'
  | 'failed'

export interface BookingAddress {
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
}

export interface BookingContact {
  company?: string
  name?: string
  phoneNumber?: string
  emailAddress?: string
}

export interface BookingStop {
  /** Absent when Curri holds no address for that end — the response Stop
   *  allows neither rather than fabricating an address of empty strings. */
  address?: BookingAddress
  /** Absent when Curri names nobody for that end, for the same reason. */
  contact?: BookingContact
  timeWindow?: { start?: string; end?: string }
  notes?: string
}

// Imperial, matching the published response Item; every measurement optional
// because a stored row may carry none. A piece count above one is appended to
// the description as `(x12)` rather than carried in a field.
export interface BookingItem {
  description?: string
  lengthIn?: number
  widthIn?: number
  heightIn?: number
  weightLbs?: number
}

/** Curri's own references for a booking, as a map keyed by name — a name is
 *  unique within a booking, and an absent one is omitted. Loose on the wire:
 *  more names are planned. */
export interface BookingReferenceNumbers {
  orderNumber?: string
  poNumber?: string
  bolNumber?: string
  [name: string]: string | undefined
}

/** The published status object — `{ code, reasonCode, note }`, the one
 *  vocabulary the payload request, response and domain share (INT-475). The
 *  bookings response carries no `note`: the union query selects none. */
export interface BookingStatusDetail {
  code: BookingStatus
  /** Only on `failed`, and only when the reason is published. */
  reasonCode?: string
}

export interface Booking {
  /** `payload_*` or `del_*`, by source. */
  id: string
  status: BookingStatusDetail
  accountId: string | null
  customerData?: Record<string, unknown>
  referenceNumbers: BookingReferenceNumbers
  pickup: BookingStop
  dropoff: BookingStop
  items: BookingItem[]
  createdAt: string
  source: BookingSource
  isSandbox: boolean
  /** The join key for the per-booking webhook attempts read. Null on a payload
   *  row, which has no delivery yet. */
  deliveryExternalId: string | null
  /** Integer USD cents; null when unpriced (every payload row today). */
  price: number | null
  vehicle: string | null
}

export interface BookingPage {
  bookings: Booking[]
  /** Opaque keyset cursor; null on the last page. */
  nextCursor: string | null
}

/** The dashboard reads the caller's own freight, which is what `payload:read`
 *  already covers — the surface deliberately does not carry a second scope for
 *  the same data. */
export const PAYLOAD_READ_SCOPE = 'payload:read'
export const PAGE_SIZE = 25

/** The statuses the server-side filter is offered for. One status per query —
 *  the multi-status "in transit" reading stays a client-side concern. */
export type BookingStatusFilter =
  | 'all'
  | 'ready'
  | 'scheduled'
  | 'delivered'
  | 'failed'

const bookingRead = <T>(
  apiFetch: ApiFetch,
  path: string,
  init: RequestInit
): Promise<ApiResult<T>> => request(apiFetch, path, init, PAYLOAD_READ_SCOPE)

export const fetchBookings = (
  apiFetch: ApiFetch,
  params: { status: BookingStatusFilter; cursor?: string },
  signal: AbortSignal
): Promise<ApiResult<BookingPage>> => {
  const query = new URLSearchParams({ limit: String(PAGE_SIZE) })
  if (params.status !== 'all') query.set('status', params.status)
  if (params.cursor !== undefined) query.set('cursor', params.cursor)

  return bookingRead(apiFetch, `/api/bookings?${query.toString()}`, { signal })
}

// ---- the originating request (INT-488) ----

/** Why the service has no request for a booking. `beyond_retention`: the row
 *  is older than the request logs reach; `not_found`: no log could be matched
 *  to exactly this booking; `unavailable`: the logs could not be queried. */
export type BookingRequestReason = 'not_found' | 'beyond_retention' | 'unavailable'

/** How faithfully `body` reproduces what the partner sent: `json` is the
 *  validated body as logged; `logfmt` is curri-api's text serialisation parsed
 *  back, every scalar a string; `raw` is that text when it did not parse. */
export type BookingRequestFidelity = 'json' | 'logfmt' | 'raw'

export interface BookingRequest {
  receivedAt: string
  method: 'POST'
  path: '/graphql' | '/api/payloads'
  operationName?: string
  curriVersion?: string
  userAgent?: string
  requestId?: string
  statusCode?: number
  durationMs?: number
  body: unknown
  bodyFidelity: BookingRequestFidelity
}

export type BookingRequestLookup =
  | { request: BookingRequest; reason?: undefined }
  | { request: null; reason: BookingRequestReason }

/** Curri users only — the service answers 403 to anyone else, so the panel
 *  is gated on `caller.isCurriAdmin` before this is ever called. */
export const fetchBookingRequest = (
  apiFetch: ApiFetch,
  bookingId: string,
  signal: AbortSignal
): Promise<ApiResult<BookingRequestLookup>> =>
  bookingRead(
    apiFetch,
    `/api/bookings/${encodeURIComponent(bookingId)}/request`,
    { signal }
  )
